import { RecurringOption } from "./../../types/block/enum";
import { useRecoilValue } from "recoil";
import {
  correctDateToNextByDay,
  getDayOfWeekForRRULE,
  isStartDayIncludedInByDay,
  shouldCorrectDate,
} from "../../services/recurrence/recurrence.service";
import {
  BlockType,
  CreateBlockType,
  UpdateBlockType,
  UpdateRecurrenceBlockType,
} from "../../types/block/type";
import { areDateEqual } from "../../utils/common/dateTime/areDateEqual";
import { recurrenceOriginalBlockState } from "../../recoil/block/recurrence";

function useHandleRecurrenceBlock() {
  const recurrenceOriginalBlockMap = useRecoilValue(recurrenceOriginalBlockState);

  /**
   * 반복 블록 생성 시 보정로직
   * @param payload
   * @returns
   */
  const convertCreatePayloadByRecurrence = ({ payload }: { payload: CreateBlockType }) => {
    // 반복룰, 시작일, 종료일이 없는 경우 종료
    if (!payload.recurrence || !payload.start || !payload.end) return payload;

    // ByDay 반복룰 변경 시 시작일이 해당 요일과 맞지 않으면 시작일 보정
    if (
      shouldCorrectDate(payload) &&
      !isStartDayIncludedInByDay(payload.recurrence[0], payload.start)
    ) {
      const { start, end } = correctDateToNextByDay(
        payload.recurrence?.[0],
        payload.start,
        payload.end
      );

      return { ...payload, start, end };
    }

    return payload;
  };

  /**
   * 반복 블록 업데이트 시 보정로직
   * @param payload
   * @param prevBlockData
   * @returns
   */
  const convertUpdatePayloadByRecurrence = ({
    payload,
    prevBlockData,
  }: {
    payload: CreateBlockType | UpdateBlockType | UpdateRecurrenceBlockType;
    prevBlockData: BlockType;
  }) => {
    // 기존, 변경 모두 반복이 아닌 경우 얼리리턴
    if (!payload.recurrence && !prevBlockData.recurrence) {
      return payload;
    }

    const updateBlockData = { ...prevBlockData, ...payload };

    // NOTE: case 1. 반복룰 보정 : updateBlockData가 반복블록이면서 payload에 start의 날짜가 바뀐 경우
    // NOTE: 반복룰 보정은 this로 수정하는 경우에는 하지 않음
    // TODO: 반복룰 보정이 필요한 케이스를 특정해야함
    const recurrenceOptionType = payload.type;
    const areDateChanged =
      payload.start &&
      prevBlockData.start &&
      !areDateEqual(new Date(payload.start), new Date(prevBlockData.start));

    if (
      recurrenceOptionType !== RecurringOption.THIS &&
      updateBlockData.recurrence &&
      areDateChanged
    ) {
      // 1-1. 매주 반복인 경우(매주 반복이거나 반복룰의 ByDay 요일이 1개인 경우)
      const isWeeklyRecurrence = updateBlockData.recurrence[0] === "RRULE:FREQ=WEEKLY";
      const singleDayPattern = /^RRULE:FREQ=WEEKLY;BYDAY=[A-Z]{2}$/;
      if (isWeeklyRecurrence || singleDayPattern.test(updateBlockData.recurrence[0])) {
        const dayOfWeek = getDayOfWeekForRRULE(new Date(payload.start ?? ""));
        const newRecurrence = `RRULE:FREQ=WEEKLY;BYDAY=${dayOfWeek}`;

        const updateRecurrencePayload = { ...payload, recurrence: [newRecurrence] };
        return updateRecurrencePayload;
      }
    }

    // NOTE: case 2. start, end 보정
    if (updateBlockData.start && updateBlockData.end) {
      const newPayload = { ...payload };

      // 1. start, end 중 하나만 변경 시 start, end 모두 전송
      if (payload.start || payload.end) {
        newPayload.start = updateBlockData.start;
        newPayload.end = updateBlockData.end;
      }

      // 2. payload에 반복룰의 변경이 있는 경우
      // 2-1. 반복룰만 제거 시 해당 블록의 시간을 기준으로 일반블록으로 변경
      if (payload.recurrence === null) {
        newPayload.start = updateBlockData.start;
        newPayload.end = updateBlockData.end;
      }

      // 2-2. all로 반복룰 변경 시 항상 모체 start, end 포함해서 전송
      if (payload.recurrence && payload.type === RecurringOption.ALL) {
        const recurrenceOriginalBlock = recurrenceOriginalBlockMap.get(updateBlockData.originalId);
        if (recurrenceOriginalBlock) {
          newPayload.start = recurrenceOriginalBlock.start;
          newPayload.end = recurrenceOriginalBlock.end;
        } else {
          newPayload.start = updateBlockData.start;
          newPayload.end = updateBlockData.end;
        }
      }

      // 2-3. ByDay 반복룰 변경 시 시작일이 해당 요일과 맞지 않으면 시작일 보정
      if (
        payload.recurrence?.[0] &&
        shouldCorrectDate(payload) &&
        !isStartDayIncludedInByDay(payload.recurrence[0], updateBlockData.start)
      ) {
        const currentStart = newPayload.start ? newPayload.start : updateBlockData.start;
        const currentEnd = newPayload.end ? newPayload.end : updateBlockData.end;

        const { start, end } = correctDateToNextByDay(
          payload.recurrence?.[0],
          currentStart,
          currentEnd
        );

        newPayload.start = start;
        newPayload.end = end;
      }

      return newPayload;
    }

    return payload;
  };

  return { convertCreatePayloadByRecurrence, convertUpdatePayloadByRecurrence };
}

export default useHandleRecurrenceBlock;
